import { skipToken } from "@reduxjs/toolkit/query";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useMemo, useState } from "react";
import { ReactComponent as AmexLogo } from "@/assets/icons/amex-color.svg";
import { ReactComponent as DinersLogo } from "@/assets/icons/diners-color.svg";
import { ReactComponent as DiscoverLogo } from "@/assets/icons/discover-color.svg";
import { ReactComponent as JcbLogo } from "@/assets/icons/jcb-color.svg";
import { ReactComponent as MastercardLogo } from "@/assets/icons/mastercard-color.svg";
import { ReactComponent as UnionPayLogo } from "@/assets/icons/unionpay-color.svg";
import { ReactComponent as VisaLogo } from "@/assets/icons/visa-color.svg";
import { Button, Icon, Loading, Typography } from "@/components/atoms";
import { useGetGuestPaymentKeysQuery } from "@/redux/apis/guest/guestApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setGuestPaymentMethodId } from "@/redux/slices/clientBooking/clientBookingSlice";
import { selectGuestPaymentMethodId } from "@/redux/slices/clientBooking/selectors";
import { GuestCardDisplay } from "@/redux/slices/clientBooking/types";
import { configSelector } from "@/redux/slices/config/selectors";
import { operatorIdSelector } from "@/redux/slices/operator/selectors";
import { addToast, clsx } from "@/utils";

interface GuestPaymentPanelProps {
  className?: string;
  /** Return to the Trip Details step. */
  onPrevious: () => void;
  /** Open the Booking Summary slide-in (below lg the summary holds Book Trip). */
  onViewSummary?: () => void;
}

// Accepted card networks shown in the "safe & secure checkout" trust badge, in Figma order — mirrors
// AuthedPaymentPanel's badge so the guest card step looks identical to the authed one. Presentational
// only; Stripe's PaymentElement renders its own accepted-brand row inside the card entry itself.
const CARD_BRANDS = [
  { name: "Visa", Logo: VisaLogo },
  { name: "Mastercard", Logo: MastercardLogo },
  { name: "American Express", Logo: AmexLogo },
  { name: "JCB", Logo: JcbLogo },
  { name: "Discover", Logo: DiscoverLogo },
  { name: "Diners Club", Logo: DinersLogo },
  { name: "UnionPay", Logo: UnionPayLogo },
];

const CardTrustBadge = () => (
  <div className="rounded-lg border border-neutral-mid-gray bg-white p-3">
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-center gap-1.5">
        <Icon name="Lock1" variant="Bold" size={16} className="shrink-0 text-neutral-black" />
        <Typography variant="small" className="!font-medium text-neutral-dark-gray">
          Guaranteed <span className="font-bold text-neutral-black">safe &amp; secure</span> checkout
        </Typography>
      </div>
      <span className="flex shrink-0 items-center gap-1 rounded bg-neutral-black px-2 py-1">
        <span className="text-[10px] leading-none text-white/60">Powered by</span>
        <span className="text-[13px] font-bold leading-none text-white">stripe</span>
      </span>
    </div>
    <hr className="my-2.5 border-neutral-mid-gray" />
    <ul className="flex items-center gap-1.5">
      {CARD_BRANDS.map(({ name, Logo }) => (
        <li key={name} className="flex h-7 flex-1 items-center justify-center rounded border border-neutral-mid-gray">
          <Logo role="img" aria-label={name} className="h-4 w-auto max-w-[85%]" />
        </li>
      ))}
    </ul>
  </div>
);

/**
 * Guest card capture. The guest has no account and no SetupIntent, so instead of the authed
 * SetupIntent-backed CardFormModal we use Stripe's DEFERRED mode: `Elements` is initialised with
 * `{ mode: "setup", currency, paymentMethodCreation: "manual" }` (no clientSecret), rendering the
 * same modern `PaymentElement` as CardFormModal. On save we `elements.submit()` to validate, then
 * `stripe.createPaymentMethod({ elements })` to mint a `pm_…` that's stored on the draft
 * (`guestPaymentMethodId`). The card is created on the PLATFORM account, so `stripe_account` is NOT
 * passed to loadStripe. At submit the server attaches that pm to the account it creates.
 * No saved payment methods are ever read here.
 */
const GuestCardForm = () => {
  const dispatch = useAppDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const savedPmId = useAppSelector(selectGuestPaymentMethodId);

  const [isSaving, setIsSaving] = useState(false);
  const [complete, setComplete] = useState(false);

  const handleSave = async () => {
    if (!stripe || !elements) return;

    setIsSaving(true);
    // Deferred mode: validate the entered fields first (required before createPaymentMethod).
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setIsSaving(false);
      addToast("danger", submitError.message || "We couldn't save that card. Please check the details and try again.");
      return;
    }

    // Tokenise straight off the Elements instance — no SetupIntent/customer exists for a guest.
    const { paymentMethod, error } = await stripe.createPaymentMethod({ elements });
    setIsSaving(false);

    if (error || !paymentMethod) {
      addToast("danger", error?.message || "We couldn't save that card. Please check the details and try again.");
      return;
    }
    // Carry the pm_ (+ the card's issuing country, for the guest quote's local/international
    // processing-fee rate, + display metadata for the confirmation screen's Payment box) on the
    // draft; the guest create endpoint attaches the pm to the minted account.
    const card: GuestCardDisplay = {
      brand: paymentMethod.card?.brand ?? "",
      last4: paymentMethod.card?.last4 ?? "",
      expMonth: paymentMethod.card?.exp_month ?? 0,
      expYear: paymentMethod.card?.exp_year ?? 0,
    };
    dispatch(setGuestPaymentMethodId({ id: paymentMethod.id, country: paymentMethod.card?.country ?? null, card }));
    addToast("success", "Card ready for your booking");
  };

  return (
    <div className="flex flex-col gap-4">
      <PaymentElement
        options={{
          paymentMethodOrder: ["card"],
          layout: { type: "tabs", defaultCollapsed: false },
          // the branded legal copy below the form replaces Stripe's own terms text (matches CardFormModal)
          terms: { card: "never" },
        }}
        onChange={(e) => setComplete(e.complete)}
      />

      {savedPmId && (
        <div className="flex items-center gap-2 rounded-lg border border-success bg-success-light px-2.5 py-1.5">
          <Icon name="TickCircle" variant="Bold" size="lg" className="shrink-0 text-success" />
          <Typography variant="action" className="text-success-dark">
            Card saved for this booking
          </Typography>
        </div>
      )}

      <Button variant="primary" size="lg" className="w-full" disabled={!complete || isSaving} onClick={handleSave}>
        {savedPmId ? "Update card" : "Save card"}
      </Button>
    </div>
  );
};

export const GuestPaymentPanel = ({ className, onPrevious, onViewSummary }: GuestPaymentPanelProps) => {
  const providerUuid = useAppSelector(operatorIdSelector);
  const config = useAppSelector(configSelector);
  const { data: keys, isLoading, isError, refetch } = useGetGuestPaymentKeysQuery(providerUuid ?? skipToken);

  const operatorName = (config?.name || "your operator").replace(/\.+$/, "");
  const termsUrl = config?.urls?.terms;

  // One Stripe instance per publishable key. The card is tokenised on the PLATFORM account, so we load
  // Stripe with the publishable key ALONE — passing stripeAccount would scope the pm_ to the operator's
  // connected account, but the server attaches it to the platform customer it mints for the guest.
  const stripePromise = useMemo(() => (keys?.publishable_key ? loadStripe(keys.publishable_key) : null), [keys?.publishable_key]);

  return (
    <div className={clsx("flex flex-col bg-white md:rounded-2xl md:border md:border-neutral-mid-gray md:shadow-lg", className)}>
      <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto p-5">
        <div>
          <Typography variant="h2">Payment</Typography>
          <Typography variant="muted" className="mt-1 block text-neutral-dark-gray">
            Enter a credit card to complete your booking
          </Typography>
        </div>

        {isLoading ? (
          <div className="relative min-h-[120px]">
            <Loading />
          </div>
        ) : isError || !stripePromise ? (
          // Real retry — without it the guest is stranded (no card form, nothing re-triggers the key fetch).
          <div className="flex flex-col items-start gap-3">
            <Typography variant="paragraph" className="text-danger">
              Unable to start the card form. Please try again.
            </Typography>
            <Button variant="tertiary" size="md" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : (
          // Deferred (SetupIntent-less) Elements — the guest has no account/customer/clientSecret yet.
          <Elements stripe={stripePromise} options={{ mode: "setup", currency: "aud", paymentMethodCreation: "manual" }}>
            <GuestCardForm />
          </Elements>
        )}

        {/* Pinned to the bottom of the scroll area, above the footer — mirrors AuthedPaymentPanel. */}
        <div className="mt-auto flex flex-col gap-3">
          <div className="flex flex-col gap-3 rounded-lg border border-info bg-info-light px-2 py-1.5">
            <Typography variant="small" className="!font-medium text-info-dark">
              Your card will be charged after your trip is completed, however some operators reserve the right to pre-charge on select
              trips.
            </Typography>
            <Typography variant="small" className="!font-medium text-info-dark">
              You can view, edit or cancel your booking at any time in accordance with {operatorName}&apos;s{" "}
              {termsUrl ? (
                <a href={termsUrl} target="_blank" rel="noreferrer" className="underline">
                  Terms and Conditions
                </a>
              ) : (
                <span className="underline">Terms and Conditions</span>
              )}
              .
            </Typography>
          </div>

          <CardTrustBadge />
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-neutral-mid-gray p-4 md:border-t-0 md:pt-0">
        <Button variant="secondary" size="md" className="flex-1 md:flex-none" onClick={onPrevious}>
          Previous Step
        </Button>
        {onViewSummary && (
          <Button variant="primary" size="md" className="flex-1 md:flex-none lg:hidden" onClick={onViewSummary}>
            View Summary
          </Button>
        )}
      </div>
    </div>
  );
};
